import { createFileRoute } from "@tanstack/react-router";
import {
  BadgeDollarSignIcon,
  BarChart3Icon,
  CoinsIcon,
  FileTextIcon,
  HourglassIcon,
  QrCodeIcon,
  Settings2Icon,
  ShieldIcon,
  SparklesIcon,
  UsersIcon,
} from "lucide-react";
import { AiToolsList } from "@/components/admin/ai-tools/ai-tools-list";
import { CreateToolDialog } from "@/components/admin/ai-tools/create-tool-dialog";
import { AttendanceLimitsSettings } from "@/components/admin/attendance-limits-settings";
import { PlatformToolsList } from "@/components/admin/custom-tools/platform-tools-list";
import { EmployeeBillingSettings } from "@/components/admin/employee-billing-settings";
import { GoogleAnalyticsSettings } from "@/components/admin/google-analytics-settings";
import { PricingSettings } from "@/components/admin/pricing-settings";
import { SafeguardSettings } from "@/components/admin/safeguards/safeguard-settings";
import { SampleRosterSettings } from "@/components/admin/sample-roster-settings";
import { SignupCreditsSettings } from "@/components/admin/signup-credits-settings";
import { TrialLifecycleSettings } from "@/components/admin/trial-lifecycle-settings";
import { requireAdminSection } from "@/lib/admin-guard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/ui/tabs";

export const Route = createFileRoute("/_admin/settings/")({
  beforeLoad: ({ context }) => requireAdminSection(context.auth.capabilities),
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-10 p-6">
      <header className="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Settings2Icon className="size-3.5" />
            Configuration
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="font-heading text-3xl font-semibold tracking-tight">
              Tools &amp; generators
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Manage the document generators available to every workspace. Edit a tool’s details,
              prompt and fields, then publish when it’s ready.
            </p>
          </div>
        </div>
        <CreateToolDialog />
      </header>

      <Tabs defaultValue="ai-tools" className="flex flex-col gap-6">
        <TabsList>
          <TabsTrigger value="ai-tools">
            <SparklesIcon className="size-4" />
            AI generator tools
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileTextIcon className="size-4" />
            Template tools
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <BadgeDollarSignIcon className="size-4" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="plan-limits">
            <QrCodeIcon className="size-4" />
            Plan limits
          </TabsTrigger>
          <TabsTrigger value="signup-credits">
            <CoinsIcon className="size-4" />
            Signup &amp; credits
          </TabsTrigger>
          <TabsTrigger value="trial-lifecycle">
            <HourglassIcon className="size-4" />
            Trial lifecycle
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3Icon className="size-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="safeguards">
            <ShieldIcon className="size-4" />
            Safeguards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai-tools" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <SparklesIcon className="size-5 text-primary" />
              AI generator tools
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Prompt-driven document generators. Open one to edit its details (name, category,
              icon), prompt and fields. Changes stay as a draft until you publish — every published
              version is kept for rollback.
            </p>
          </div>
          <AiToolsList />
        </TabsContent>

        <TabsContent value="templates" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <FileTextIcon className="size-5 text-primary" />
              Template tools
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Fill-in templates (no AI) you publish to every workspace. They appear read-only in
              each workspace’s “My tools” list.
            </p>
          </div>
          <PlatformToolsList />
        </TabsContent>

        <TabsContent value="pricing" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <BadgeDollarSignIcon className="size-5 text-primary" />
              Pricing
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Which set of plan limits is live, and every plan's number for every limit. One account
              can be given its own numbers from its user page.
            </p>
          </div>
          <PricingSettings />
        </TabsContent>

        <TabsContent value="plan-limits" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <QrCodeIcon className="size-5 text-primary" />
              Attendance sessions per month
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              How many attendance sessions each plan can create in a monthly billing window. Give
              one account its own number from its user page. The pricing page keeps showing the
              defaults.
            </p>
          </div>
          <AttendanceLimitsSettings />

          <div className="flex flex-col gap-1.5 pt-4">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <UsersIcon className="size-5 text-primary" />
              Per-employee billing
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Business accounts pay for each employee past the included headcount. Their headcount
              is reported to Polar, which bills it. Leave this on dry run until the counts look
              right, then switch to live.
            </p>
          </div>
          <EmployeeBillingSettings />
        </TabsContent>

        <TabsContent value="signup-credits" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <CoinsIcon className="size-5 text-primary" />
              Signup &amp; credits
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              The trial and AI credits every new account starts with, and the credits each plan
              grants per month. Give one account its own numbers from its user page.
            </p>
          </div>
          <SignupCreditsSettings />
          <SampleRosterSettings />
        </TabsContent>

        <TabsContent value="trial-lifecycle" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <HourglassIcon className="size-5 text-primary" />
              Trial lifecycle
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              What accounts hear about their trial: the emails before and after it ends, the banner
              once it has ended, and a discount to bring them back.
            </p>
          </div>
          <TrialLifecycleSettings />
        </TabsContent>

        <TabsContent value="analytics" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <BarChart3Icon className="size-5 text-primary" />
              Google Analytics
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              The GA4 properties the customer app reports to. The tag loads only for visitors who
              accept analytics cookies, and pages are reported by route, never by their real URL.
            </p>
          </div>
          <GoogleAnalyticsSettings />
        </TabsContent>

        <TabsContent value="safeguards" className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
              <ShieldIcon className="size-5 text-primary" />
              Sign-up safeguards
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Protections against throwaway and automated sign-ups. Each plan's daily email
              allowance is one of its plan limits.
            </p>
          </div>
          <SafeguardSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
